import { arrayMove } from '@dnd-kit/sortable'
import { DataTableColumn, DataTableSortState, DataTableSortValue } from './DataTable.types'

const normalizeSortValue = (value: DataTableSortValue) => {
    if (typeof value === 'string') {
        return value.trim().toLocaleLowerCase()
    }

    if (typeof value === 'boolean') {
        return value ? 1 : 0
    }

    return value ?? ''
}

const isMissingSortValue = (value: DataTableSortValue) =>
    value === null || value === undefined || (typeof value === 'string' && !value.trim())

const compareSortValues = (left: DataTableSortValue, right: DataTableSortValue) => {
    const normalizedLeft = normalizeSortValue(left)
    const normalizedRight = normalizeSortValue(right)

    if (typeof normalizedLeft === 'string' && typeof normalizedRight === 'string') {
        return normalizedLeft.localeCompare(normalizedRight, undefined, { sensitivity: 'base' })
    }

    if (normalizedLeft < normalizedRight) return -1
    if (normalizedLeft > normalizedRight) return 1

    return 0
}

const toComparators = <TRow, TColumnId extends string>(
    columns: DataTableColumn<TRow, TColumnId>[],
    sortState: DataTableSortState<TColumnId>
) =>
    sortState.flatMap((key) => {
        const getSortValue = columns.find((column) => column.id === key.columnId)?.getSortValue

        return getSortValue ? [{ getSortValue, directionMultiplier: key.direction === 'asc' ? 1 : -1 }] : []
    })

export const sortDataTableRows = <TRow, TColumnId extends string>(
    rows: TRow[],
    columns: DataTableColumn<TRow, TColumnId>[],
    sortState: DataTableSortState<TColumnId>
) => {
    const comparators = toComparators(columns, sortState)

    if (!comparators.length) {
        return rows
    }

    return [...rows].sort((left, right) => {
        for (const { getSortValue, directionMultiplier } of comparators) {
            const leftValue = getSortValue(left)
            const rightValue = getSortValue(right)
            const leftIsMissing = isMissingSortValue(leftValue)
            const rightIsMissing = isMissingSortValue(rightValue)

            // Not multiplied by the direction: reversing a column must not lift its blanks to the top.
            if (leftIsMissing !== rightIsMissing) return leftIsMissing ? 1 : -1
            if (leftIsMissing) continue

            const order = compareSortValues(leftValue, rightValue) * directionMultiplier

            if (order !== 0) return order
        }

        return 0
    })
}

export const getNoMatchesText = (entityLabel: string) => `No ${entityLabel} match the current search or filters.`

export const reorderRowsById = <TRow>(
    rows: TRow[],
    getRowId: (row: TRow) => string,
    activeRowId: string,
    overRowId: string
) => {
    if (activeRowId === overRowId) return null

    const rowIds = rows.map(getRowId)
    const fromIndex = rowIds.indexOf(activeRowId)
    const toIndex = rowIds.indexOf(overRowId)

    if (fromIndex < 0 || toIndex < 0) return null

    return arrayMove(rows, fromIndex, toIndex)
}
